import type { createBusinessDayCoordinator } from '../business-day/business-day-coordinator.ts';
import type { StaffPermission } from '../staff/staff-authority.ts';
import type {
  SmtBusinessDayDispatchResult,
  SmtBusinessDayIntent,
  SmtBusinessDayMutationContext,
  SmtBusinessDayRequest,
  SmtBusinessDayViewModel,
} from './smt-business-day-contract.ts';

type BusinessDayCoordinator = ReturnType<typeof createBusinessDayCoordinator>;

export type SmtBusinessDayPort = Pick<
  BusinessDayCoordinator,
  | 'projection'
  | 'openBusinessDay'
  | 'openService'
  | 'closeService'
  | 'setCustomerIntake'
  | 'setCustomerEta'
  | 'customerEtaAdvisory'
  | 'planEarlyClose'
  | 'cancelEarlyClose'
  | 'recordCashMovement'
  | 'recordHandover'
  | 'closeBusinessDay'
  | 'reprintCloseReceipt'
>;

export interface SmtBusinessDayStaffAuthorizationRequest {
  readonly actorId: string;
  readonly permission: Extract<
    StaffPermission,
    'business-day.open' | 'service.manage' | 'cash.movement' | 'shift.close' | 'manager.override'
  >;
  readonly at: string;
}

export interface CreateSmtBusinessDaySurfaceDependencies {
  readonly getCoordinator: () => SmtBusinessDayPort | null;
  readonly authorizeStaff?: (request: SmtBusinessDayStaffAuthorizationRequest) => void;
}

const assertRequest = (request: SmtBusinessDayRequest): void => {
  if (!request.actorId?.trim()) throw new Error('SMT_BUSINESS_DAY_ACTOR_REQUIRED');
  if (!Number.isFinite(Date.parse(request.at))) throw new Error('SMT_BUSINESS_DAY_TIMESTAMP_INVALID');
};

const assertMutationContext = (context: SmtBusinessDayMutationContext): void => {
  assertRequest(context);
  if (!Number.isSafeInteger(context.expectedRevision) || context.expectedRevision < 0) {
    throw new Error('SMT_BUSINESS_DAY_EXPECTED_REVISION_INVALID');
  }
  if (!context.idempotencyKey?.trim()) throw new Error('SMT_BUSINESS_DAY_IDEMPOTENCY_REQUIRED');
};

const requireCoordinator = (deps: CreateSmtBusinessDaySurfaceDependencies): SmtBusinessDayPort => {
  const coordinator = deps.getCoordinator();
  if (!coordinator) throw new Error('SMT_BUSINESS_DAY_AUTHORITY_REQUIRED');
  return coordinator;
};

const permissionHint = (
  deps: CreateSmtBusinessDaySurfaceDependencies,
  request: SmtBusinessDayRequest,
  permission: SmtBusinessDayStaffAuthorizationRequest['permission'],
): boolean => {
  if (!deps.authorizeStaff) return false;
  try {
    deps.authorizeStaff({ actorId: request.actorId, permission, at: request.at });
    return true;
  } catch {
    return false;
  }
};

const project = (
  coordinator: SmtBusinessDayPort,
  request: SmtBusinessDayRequest,
  deps: CreateSmtBusinessDaySurfaceDependencies,
): SmtBusinessDayViewModel | null => {
  const snapshot = coordinator.projection();
  if (!snapshot) return null;
  return {
    ...structuredClone(snapshot),
    etaAdvisory: structuredClone(coordinator.customerEtaAdvisory()),
    permissions: {
      canOpenBusinessDay: permissionHint(deps, request, 'business-day.open'),
      canManageService: permissionHint(deps, request, 'service.manage'),
      canRecordCashMovement: permissionHint(deps, request, 'cash.movement'),
      canCloseShift: permissionHint(deps, request, 'shift.close'),
      canManagerOverride: permissionHint(deps, request, 'manager.override'),
    },
  };
};

const knownBusinessErrorCode = (error: unknown): string | null => {
  if (!(error instanceof Error)) return null;
  if (error.message.startsWith('BUSINESS_DAY_')) return error.message;
  if (error.message.startsWith('DAY_CLOSE_')) return error.message;
  if (error.message.startsWith('STAFF_')) return error.message;
  return null;
};

export function createSmtBusinessDaySurface(deps: CreateSmtBusinessDaySurfaceDependencies) {
  const snapshot = (request: SmtBusinessDayRequest): SmtBusinessDayViewModel | null => {
    assertRequest(request);
    return project(requireCoordinator(deps), request, deps);
  };

  const dispatch = async (
    intent: SmtBusinessDayIntent,
    context: SmtBusinessDayMutationContext,
  ): Promise<SmtBusinessDayDispatchResult> => {
    assertMutationContext(context);
    const coordinator = requireCoordinator(deps);
    let receipt: Awaited<ReturnType<SmtBusinessDayPort['closeBusinessDay']>> | undefined;
    let printJob: Awaited<ReturnType<SmtBusinessDayPort['reprintCloseReceipt']>> | undefined;

    try {
      switch (intent.kind) {
        case 'business-day.open':
          await coordinator.openBusinessDay({
            businessDate: intent.businessDate,
            drawerId: intent.drawerId,
            openingCashMinor: intent.openingCashMinor,
          }, context);
          break;
        case 'service.open':
          await coordinator.openService(context);
          break;
        case 'service.close':
          await coordinator.closeService({ reason: intent.reason }, context);
          break;
        case 'customer-intake.set':
          await coordinator.setCustomerIntake({ status: intent.status, reason: intent.reason }, context);
          break;
        case 'customer-eta.set':
          await coordinator.setCustomerEta({ minMinutes: intent.minMinutes, maxMinutes: intent.maxMinutes }, context);
          break;
        case 'early-close.plan':
          await coordinator.planEarlyClose({ effectiveAt: intent.effectiveAt, reason: intent.reason }, context);
          break;
        case 'early-close.cancel':
          await coordinator.cancelEarlyClose(context);
          break;
        case 'cash.movement.record':
          await coordinator.recordCashMovement({
            movementId: intent.movementId,
            direction: intent.direction,
            amountMinor: intent.amountMinor,
            reason: intent.reason,
          }, context);
          break;
        case 'cash-shift.handover':
          await coordinator.recordHandover({
            handoverId: intent.handoverId,
            fromStaffId: intent.fromStaffId,
            toStaffId: intent.toStaffId,
            countedCashMinor: intent.countedCashMinor,
          }, context);
          break;
        case 'business-day.close':
          receipt = await coordinator.closeBusinessDay({
            closeId: intent.closeId,
            actorId: context.actorId,
            at: context.at,
            expectedRevision: context.expectedRevision,
            idempotencyKey: context.idempotencyKey,
            countedCashMinor: intent.countedCashMinor,
            closingWithdrawalMinor: intent.closingWithdrawalMinor,
            varianceRecheckCompleted: intent.varianceRecheckCompleted,
            override: intent.override,
            print: intent.print,
          });
          break;
        case 'day-close.reprint':
          printJob = await coordinator.reprintCloseReceipt({
            businessDate: intent.businessDate,
            closeId: intent.closeId,
            intentId: intent.intentId,
            requestedBy: context.actorId,
            reason: intent.reason,
            at: context.at,
            templateVersion: intent.templateVersion,
            payloadVersion: intent.payloadVersion,
            targets: intent.targets,
          });
          break;
        default: {
          const exhaustive: never = intent;
          throw new Error(`SMT_BUSINESS_DAY_INTENT_UNSUPPORTED:${String(exhaustive)}`);
        }
      }
    } catch (error: unknown) {
      const code = knownBusinessErrorCode(error);
      if (!code) throw error;
      return { kind: 'rejected', code, snapshot: project(coordinator, context, deps) };
    }

    const refreshed = project(coordinator, context, deps);
    if (!refreshed) throw new Error('SMT_BUSINESS_DAY_ACCEPTED_STATE_MISSING');
    return {
      kind: 'accepted',
      snapshot: refreshed,
      ...(receipt ? { receipt: structuredClone(receipt) } : {}),
      ...(printJob ? { printJob: structuredClone(printJob) } : {}),
    };
  };

  return { snapshot, dispatch };
}
